import { apiFetch } from "./client";

export type ComplaintStatus = "SUBMITTED" | "IN_REVIEW" | "RESOLVED" | "REJECTED" | "CLOSED";

/** What the complaint is about; mirrors `ComplaintKind` and `chk_complaint_kind`. */
export type ComplaintKind =
  | "QUALITY"
  | "QUANTITY"
  | "TRANSPORT"
  | "SHELF_LIFE"
  | "FOREIGN_BODY"
  | "DOCUMENTATION";

export const COMPLAINT_KINDS: ComplaintKind[] = [
  "QUALITY", "QUANTITY", "TRANSPORT", "SHELF_LIFE", "FOREIGN_BODY", "DOCUMENTATION",
];

/** How the complaint is settled; mirrors `ComplaintOutcome` and `chk_complaint_outcome`. */
export type ComplaintOutcome =
  | "CREDIT_NOTE"
  | "REPLACEMENT"
  | "RETURN_AND_REFUND"
  | "DISCOUNT_NEXT"
  | "NO_COMPENSATION";

export const COMPLAINT_OUTCOMES: ComplaintOutcome[] = [
  "CREDIT_NOTE", "REPLACEMENT", "RETURN_AND_REFUND", "DISCOUNT_NEXT", "NO_COMPENSATION",
];

/** The three outcomes an amount may accompany, exactly as `chk_complaint_iznos` allows. */
export function outcomeCarriesAmount(outcome: ComplaintOutcome): boolean {
  return outcome === "CREDIT_NOTE" || outcome === "RETURN_AND_REFUND" || outcome === "DISCOUNT_NEXT";
}

/**
 * Reklamacije na kvalitet (quality complaints) — mirrors
 * `ComplaintDtos.ComplaintView` on the backend exactly. Deliberately carries
 * NO monetary/commercial fields (KVALITET must never see prices via this
 * module — design); the referenced order is exposed only as
 * id + order number, never its lines/pricing.
 */
export type Complaint = {
  id: string;
  /** REK-YYYY-NNNN — what a letter or a phone call quotes. */
  caseNumber: string;
  companyId: string;
  companyName: string;
  title: string;
  description: string | null;
  orderId: string | null;
  orderNumber: string | null;
  lot: string | null;
  /** Structured link to an intake lot; the free-text `lot` above stays for older rows.
   * Internal traceability: always null for a customer. */
  receivingLotId: string | null;
  receivingLotNumber: string | null;
  status: ComplaintStatus;
  /** Present on a rejected complaint; the customer reads it on the complaint page. */
  rejectReason: string | null;
  /** Null on a complaint filed before the kind existed. */
  kind: ComplaintKind | null;
  urgent: boolean;
  goodsLotId: string | null;
  productId: string | null;
  dispatchNoteId: string | null;
  claimedQuantity: string | null;
  claimedUnit: string | null;
  goodsReceivedOn: string | null;
  /** 24 h from filing, calendar time (workshop 11.09.2026). */
  dueFirstResponseAt: string | null;
  firstResponseAt: string | null;
  /** Seven days from filing, calendar time. */
  dueFinalAt: string | null;
  finalResponseAt: string | null;
  decidedBy: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionBasis: string | null;
  outcome: ComplaintOutcome | null;
  /** The one commercial figure in this module; null for a caller who may not see it. */
  outcomeAmount: string | null;
  outcomeCurrency: string | null;
  outcomeApprovedBy: string | null;
  outcomeApprovedByName: string | null;
  outcomeApprovedAt: string | null;
  eightDOpenedAt: string | null;
  eightDOpenedByName: string | null;
  eightDReason: string | null;
  closedAt: string | null;
  closedByName: string | null;
  /** The closed complaint this one grew out of, when the buyer contested the answer. */
  reopenedFromId: string | null;
  reopenedFromCaseNumber: string | null;
  reopenedAt: string | null;
  /** The complaint that grew out of THIS one; only filled on the single-complaint read. */
  reopenedAsId: string | null;
  reopenedAsCaseNumber: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

/** One of Bankom's own people, for the pickers that name a team or an owner. */
export type ComplaintStaffOption = { id: string; name: string };

export type ComplaintTeamMember = {
  id: string;
  complaintId: string;
  userId: string;
  userName: string | null;
  roleInTeam: string | null;
  assignedByName: string | null;
  assignedAt: string;
  removedAt: string | null;
};

export type CorrectiveAction = {
  id: string;
  complaintId: string;
  description: string;
  rootCause: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  dueOn: string | null;
  completedAt: string | null;
  completedByName: string | null;
  evidenceNote: string | null;
  effectivenessCheckedAt: string | null;
  effectivenessCheckedByName: string | null;
  effectivenessNote: string | null;
  createdByName: string | null;
  createdAt: string;
};

export type EightDStep = {
  id: string;
  complaintId: string;
  /** 1 to 8. */
  step: number;
  content: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  dueOn: string | null;
  completedAt: string | null;
  completedByName: string | null;
  updatedAt: string;
};

/** `companyId` is required for ADMIN/SUPERADMIN callers filing on a
 * customer's behalf; CUST_* callers may omit it (defaults to their own
 * company) — the backend resolves and validates this per caller role. */
export type CreateComplaintRequest = {
  companyId?: string | null;
  title: string;
  description?: string | null;
  orderId?: string | null;
  lot?: string | null;
  kind?: ComplaintKind | null;
  urgent?: boolean | null;
  goodsLotId?: string | null;
  productId?: string | null;
  dispatchNoteId?: string | null;
  claimedQuantity?: string | null;
  claimedUnit?: string | null;
  goodsReceivedOn?: string | null;
};

/** One step of the status timeline; `changedByName` is null for a system row. */
export type ComplaintStatusHistoryEntry = {
  status: ComplaintStatus;
  changedAt: string;
  changedByName: string | null;
  note: string | null;
};

export type ComplaintMessage = {
  id: string;
  complaintId: string;
  authorUserId: string;
  authorName: string;
  body: string;
  /** A note the team writes for itself; the buyer's thread never contains one. */
  internal: boolean;
  createdAt: string;
};

export type ComplaintAttachment = {
  id: string;
  complaintId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: string;
  uploadedByName: string;
  createdAt: string;
};

export type ComplaintPresignResponse = { objectKey: string; url: string };

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export const complaintsApi = {
  /** Points a complaint at an intake lot. Null clears it; nothing else changes. */
  linkLot: (id: string, receivingLotId: string | null) =>
    apiFetch<Complaint>(`/complaints/${id}/lot`, { method: "PUT", body: { receivingLotId } }),
  list: (params: { companyId?: string; status?: ComplaintStatus } = {}) =>
    apiFetch<Complaint[]>(`/complaints${q(params)}`),
  get: (id: string) => apiFetch<Complaint>(`/complaints/${id}`),
  history: (id: string) => apiFetch<ComplaintStatusHistoryEntry[]>(`/complaints/${id}/history`),
  create: (body: CreateComplaintRequest) =>
    apiFetch<Complaint>("/complaints", { method: "POST", body }),

  listMessages: (id: string) => apiFetch<ComplaintMessage[]>(`/complaints/${id}/messages`),
  addMessage: (id: string, body: string, internal = false) =>
    apiFetch<ComplaintMessage>(`/complaints/${id}/messages`, { method: "POST", body: { body, internal } }),

  transition: (id: string, to: ComplaintStatus, reason?: string, basis?: string) =>
    apiFetch<Complaint>(`/complaints/${id}/transition`, { method: "POST", body: { to, reason, basis } }),

  /** What the complaint is about, and whether it jumps the queue. */
  classify: (id: string, body: { kind: ComplaintKind | null; urgent?: boolean }) =>
    apiFetch<Complaint>(`/complaints/${id}/classification`, { method: "PUT", body }),

  /** An amount also needs a currency and the sales director; the backend refuses otherwise. */
  setOutcome: (id: string, body: { outcome: ComplaintOutcome; amount?: string | null; currency?: string | null }) =>
    apiFetch<Complaint>(`/complaints/${id}/outcome`, { method: "PUT", body }),

  /** Opens a fresh complaint out of a closed one the buyer contests. */
  reopen: (id: string, reason: string) =>
    apiFetch<Complaint>(`/complaints/${id}/reopen`, { method: "POST", body: { reason } }),

  /** Bankom's own people, for the pickers on a complaint. */
  staff: () => apiFetch<ComplaintStaffOption[]>("/complaints/staff"),

  listTeam: (id: string) => apiFetch<ComplaintTeamMember[]>(`/complaints/${id}/team`),
  addTeamMember: (id: string, body: { userId: string; roleInTeam?: string | null }) =>
    apiFetch<ComplaintTeamMember>(`/complaints/${id}/team`, { method: "POST", body }),
  removeTeamMember: (id: string, memberId: string) =>
    apiFetch<void>(`/complaints/${id}/team/${memberId}`, { method: "DELETE" }),

  listCorrectiveActions: (id: string) => apiFetch<CorrectiveAction[]>(`/complaints/${id}/corrective-actions`),
  createCorrectiveAction: (
    id: string,
    body: { description: string; rootCause?: string | null; ownerUserId?: string | null; dueOn?: string | null },
  ) => apiFetch<CorrectiveAction>(`/complaints/${id}/corrective-actions`, { method: "POST", body }),
  updateCorrectiveAction: (
    id: string,
    actionId: string,
    body: { description: string; rootCause?: string | null; ownerUserId?: string | null; dueOn?: string | null },
  ) => apiFetch<CorrectiveAction>(`/complaints/${id}/corrective-actions/${actionId}`, { method: "PUT", body }),
  completeCorrectiveAction: (id: string, actionId: string, evidenceNote: string) =>
    apiFetch<CorrectiveAction>(`/complaints/${id}/corrective-actions/${actionId}/complete`, {
      method: "POST",
      body: { evidenceNote },
    }),
  checkCorrectiveActionEffectiveness: (id: string, actionId: string, effectivenessNote: string) =>
    apiFetch<CorrectiveAction>(`/complaints/${id}/corrective-actions/${actionId}/effectiveness`, {
      method: "POST",
      body: { effectivenessNote },
    }),

  listEightD: (id: string) => apiFetch<EightDStep[]>(`/complaints/${id}/eight-d`),
  openEightD: (id: string, reason: string) =>
    apiFetch<EightDStep[]>(`/complaints/${id}/eight-d`, { method: "POST", body: { reason } }),
  updateEightDStep: (
    id: string,
    step: number,
    body: { content?: string | null; ownerUserId?: string | null; dueOn?: string | null },
  ) => apiFetch<EightDStep>(`/complaints/${id}/eight-d/${step}`, { method: "PUT", body }),
  completeEightDStep: (id: string, step: number) =>
    apiFetch<EightDStep>(`/complaints/${id}/eight-d/${step}/complete`, { method: "POST" }),
  reopenEightDStep: (id: string, step: number) =>
    apiFetch<EightDStep>(`/complaints/${id}/eight-d/${step}/reopen`, { method: "POST" }),

  listAttachments: (id: string) => apiFetch<ComplaintAttachment[]>(`/complaints/${id}/attachments`),
  presignAttachment: (id: string, body: { fileName: string; contentType: string; sizeBytes: number }) =>
    apiFetch<ComplaintPresignResponse>(`/complaints/${id}/attachments/presign`, { method: "POST", body }),
  confirmAttachment: (
    id: string,
    body: { objectKey: string; fileName: string; contentType: string; sizeBytes: number },
  ) => apiFetch<ComplaintAttachment>(`/complaints/${id}/attachments/confirm`, { method: "POST", body }),
  attachmentDownloadUrl: (id: string, attachmentId: string) =>
    apiFetch<{ url: string }>(`/complaints/${id}/attachments/${attachmentId}/download-url`),
};

/** Mirrors `ComplaintStatus.nextAllowed` on the backend exactly:
 * SUBMITTED -> IN_REVIEW -> RESOLVED | REJECTED -> CLOSED, with CLOSED the only terminal state. */
export function nextAllowedComplaint(from: ComplaintStatus): ComplaintStatus[] {
  switch (from) {
    case "SUBMITTED": return ["IN_REVIEW"];
    case "IN_REVIEW":  return ["RESOLVED", "REJECTED"];
    case "RESOLVED":
    case "REJECTED":   return ["CLOSED"];
    default:           return [];
  }
}

export function statusBadgePalette(status: ComplaintStatus): { bg: string; fg: string } {
  switch (status) {
    case "SUBMITTED": return { bg: "var(--info-bg)", fg: "var(--info)" };
    case "IN_REVIEW":  return { bg: "var(--warn-bg)", fg: "var(--warn)" };
    case "RESOLVED":   return { bg: "var(--ok-bg)", fg: "var(--ok)" };
    case "REJECTED":   return { bg: "var(--err-bg)", fg: "var(--err)" };
    case "CLOSED":     return { bg: "var(--surface-2)", fg: "var(--ink-2)" };
  }
  // A state the browser does not know is a state the server added since this bundle was built,
  // which is exactly what a reader with an open tab sees during a release. An unknown state is
  // shown plainly rather than taking the screen down with it.
  return { bg: "var(--surface-2)", fg: "var(--ink-2)" };
}
